<template>
    <Form as="" :validation-schema="schema" v-slot="{ meta, handleSubmit }">
        <bs5-base-modal :title="$t('clients.addTitle')" :visible="visible" @close="closeModal">
            <template v-slot:default>
                <form novalidate @submit.prevent="handleSubmit($event, addClient)">
                    <bs5-form-field v-if="!distributorId" id="addClientDistributor" name="distributorId"
                        icon="bi-shop" :label="$t('clients.distributor')" :placeholder="$t('clients.chooseDistributor')"
                        :options="distributorOptions" v-model="newClient.distributorId" />
                    <bs5-form-field id="addClientName" name="name" :label="$t('common.name')" v-model="newClient.name" />
                    <bs5-form-field id="addClientDescription" name="description" :label="$t('common.description')"
                        v-model="newClient.description" />
                    <button type="submit" class="d-none" tabindex="-1" aria-hidden="true"></button>
                </form>
            </template>

            <template v-slot:footer>
                <bs5-cancel-button @click="closeModal" />
                <bs5-save-button :loading="saving" :disabled="!meta.valid" @click="handleSubmit($event, addClient)">
                    <i class="bi bi-plus-lg"></i>{{ $t("common.add") }}
                </bs5-save-button>
            </template>
        </bs5-base-modal>
    </Form>
</template>

<script lang="ts">
import { t } from "@/helpers/i18n";
import { defineComponent, PropType } from "vue";
import { Form } from "vee-validate";
import * as yup from "yup";
import clientService from "@/services/client.service";
import { Client } from "@/models/client.model";
import { Distributor } from "@/models/distributor.model";
import { showToast } from "@/utils/toast";
import { nameMaxLength, optionalText, requiredChoice, requiredText } from "@/utils/validation";

export default defineComponent({
    name: "AddClientModal",
    components: { Form },
    props: {
        visible: { type: Boolean, required: true },
        // Pusty, gdy dystrybutor nie wynika z widoku - wtedy użytkownik wybiera go w formularzu
        distributorId: { type: String, default: "" },
        distributors: { type: Array as PropType<Distributor[]>, default: () => [] },
    },
    emits: ["close", "client-added"],
    data() {
        return {
            newClient: { name: "", description: "", distributorId: this.distributorId } as Omit<Client, "id">,
            saving: false,
        };
    },
    computed: {
        distributorOptions(): { value: string; label: string }[] {
            return this.distributors.map(distributor => ({ value: distributor.id as string, label: distributor.name }));
        },
        schema(): yup.AnyObjectSchema {
            // Wybór dystrybutora walidujemy tylko wtedy, gdy pole jest widoczne (dystrybutor nie wynika z widoku)
            return yup.object({
                ...(this.distributorId ? {} : { distributorId: requiredChoice() }),
                name: requiredText(nameMaxLength),
                description: optionalText(),
            });
        },
    },
    watch: {
        distributorId: {
            immediate: true,
            handler(newId: string) {
                this.newClient.distributorId = newId;
            },
        },
        visible(isVisible: boolean) {
            if (isVisible) {
                this.newClient = { name: "", description: "", distributorId: this.distributorId };
            }
        },
    },
    methods: {
        async addClient() {
            if (!this.newClient.distributorId || !this.newClient.name) {
                return;
            }
            this.saving = true;
            try {
                await clientService.createClient({ ...this.newClient });
                this.$emit("client-added");
                this.closeModal();
                showToast(t("clients.added"), "success");
            } catch (error) {
                showToast(t("clients.addFailed"), "error");
                console.log(error);
            } finally {
                this.saving = false;
            }
        },
        closeModal() {
            this.$emit("close");
        },
    },
});
</script>
